import React, { useCallback } from 'react';
import { cn } from '@/util/cn';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Heading } from '@/components/ui/heading';
import { getPortalOptions } from '@/config/getPortalConfig';
import { useAnalytics } from '@/tracking/analytics';
import { SanitizeMode, useSanitizedHtmlProps } from '@/util/sanitized';
import type { INews } from '@/api/home/types';

interface IRelatedArticles {
  relatedNewsList: Array<INews> | null,
  isSummerMode?: boolean,
}

const NewsItem = ({
  topic,
  href,
  description,
  imgSrc = '/assets/resort/base-related-news.jpg',
}: {
  topic: string,
  href: string,
  description: string,
  imgSrc?: string,
}) => {
  const a = useAnalytics();
  const onClickNews = useCallback(() => {
    a.viewContent({ content: 'Related News', 'Landing Page': href });
  }, [a, href]);

  const {
    dangerouslySetInnerHTML: { __html: sanitizedDescription },
  } = useSanitizedHtmlProps(description || topic || '', SanitizeMode.TextOnly);

  return (
    <div className="flex items-center gap-3 py-2">
      <img
        className="h-[60px] w-[60px] shrink-0 overflow-hidden rounded-full border border-[#F9F9F9] object-cover text-transparent"
        alt={sanitizedDescription}
        src={imgSrc}
      />
      <a
        href={href}
        className="text-sm text-foreground transition-colors hover:text-primary"
        onClick={onClickNews}
        aria-label={sanitizedDescription}
      >
        {/* eslint-disable-next-line react/no-danger -- CMS news title, rendered as HTML by design */}
        <span dangerouslySetInnerHTML={{ __html: topic }} />
      </a>
    </div>
  );
};

const RelatedArticles = ({ relatedNewsList, isSummerMode }: IRelatedArticles) => {
  const { t } = useOTSTranslation('common');

  const { hasNews } = getPortalOptions();
  if (!hasNews || !relatedNewsList || !relatedNewsList.length) {
    return null;
  }

  return (
    <section className="mb-10">
      <Heading
        level={2}
        className={cn(
          'mb-4 font-heading text-lg md:text-xl',
          isSummerMode ? 'text-primary-summer' : 'text-secondary',
        )}
      >
        {t('common.relatedNews')}
      </Heading>
      <div className="space-y-1">
        {relatedNewsList.map((item) => (
          <NewsItem
            key={item.uuid}
            topic={item.title}
            href={item.link ?? ''}
            imgSrc={item.thumbnail || item.image}
            description={item.description}
          />
        ))}
      </div>
    </section>
  );
};

export default RelatedArticles;
